const TWITCH_PLAYER_URL = 'https://player.twitch.tv/';
const TWITCH_CHAT_ORIGIN = 'https://www.twitch.tv';
const TWITCH_LOGIN_PATTERN = /^[a-zA-Z0-9_]{1,25}$/;
const IPV4_PATTERN = /^\d{1,3}(?:\.\d{1,3}){3}$/;

let configuredParentDomains: string[] = [];
let cachedParentDomains: readonly string[] | null = null;

function normalizeParentDomain(value: string): string | null {
    const candidate = value.trim().toLowerCase();
    if (!candidate) return null;

    try {
        const url = new URL(candidate.includes('://') ? candidate : `https://${candidate}`);
        return url.hostname.replace(/\.$/, '') || null;
    } catch {
        console.warn(`[Twitch embed] Domaine parent ignoré car invalide : ${value}`);
        return null;
    }
}

function assertValidLogin(login: string): string {
    const normalizedLogin = login.trim().toLowerCase();
    if (!TWITCH_LOGIN_PATTERN.test(normalizedLogin)) {
        throw new Error(`Nom de chaîne Twitch invalide : ${login}`);
    }
    return normalizedLogin;
}

function appendParentDomains(url: URL): void {
    getTwitchParentDomains().forEach((domain) => url.searchParams.append('parent', domain));
}

export function configureTwitchEmbedParents(domains: readonly string[] = []): void {
    configuredParentDomains = domains
        .map(normalizeParentDomain)
        .filter((domain): domain is string => Boolean(domain));
    cachedParentDomains = null;
}

export function getTwitchParentDomains(): readonly string[] {
    if (cachedParentDomains) return cachedParentDomains;

    const currentDomain = normalizeParentDomain(window.location.hostname);
    const domains = new Set(configuredParentDomains);
    if (currentDomain) domains.add(currentDomain);

    if (domains.size === 0) {
        throw new Error('Impossible de déterminer le domaine parent requis par Twitch.');
    }

    cachedParentDomains = Object.freeze([...domains]);
    return cachedParentDomains;
}

export function warnAboutUnsupportedEmbedOrigin(): void {
    const { hostname, protocol } = window.location;
    const isLocalDevelopment = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';

    if (protocol !== 'https:' && !isLocalDevelopment) {
        console.error(
            '[Twitch embed] Twitch exige HTTPS hors développement local. ' +
            `Origine actuelle : ${window.location.origin}`,
        );
    }

    if (hostname === '0.0.0.0') {
        console.error(
            '[Twitch embed] Ouvrez le serveur de développement via http://localhost plutôt que 0.0.0.0.',
        );
    } else if (IPV4_PATTERN.test(hostname) && !isLocalDevelopment) {
        console.error(
            '[Twitch embed] Twitch refuse les adresses IP réseau comme domaine parent. ' +
            'Utilisez `npm run dev:lan` avec un hostname HTTPS (voir README).',
        );
    }
}

export function createTwitchPlayerUrl(login: string): string {
    const url = new URL(TWITCH_PLAYER_URL);
    url.searchParams.set('channel', assertValidLogin(login));
    appendParentDomains(url);
    url.searchParams.set('autoplay', 'true');
    url.searchParams.set('muted', 'true');
    return url.toString();
}

export function createTwitchChatUrl(login: string): string {
    const channel = encodeURIComponent(assertValidLogin(login));
    const url = new URL(`/embed/${channel}/chat`, TWITCH_CHAT_ORIGIN);
    appendParentDomains(url);
    url.searchParams.set('darkpopout', 'true');
    return url.toString();
}
